import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { getApiBaseUrl } from "../utils/apiConfig";
import {
  FaPhone,
  FaStar,
  FaWifi,
  FaSwimmingPool,
  FaParking,
  FaCoffee,
  FaTshirt,
  FaSnowflake,
  FaTv,
  FaUtensils,
  FaTimes,
  FaCheckCircle,
  FaMapMarkerAlt,
  FaGem
} from "react-icons/fa";
import NavUser from "../components/NavUser";
import "./TravelerHotel.css";
import { getHotelListingById, getHotelListings } from "../services/hotelService";
import CurrencyService from "../services/currencyService";
import Booking from "../components/Booking";
import { getFileUrl } from "../utils/imageUrl";
import SEOHead from "../components/SEO/SEOHead";
import { getHotelSchema } from "../components/SEO/schemaTemplates";

const CURATED_HOTEL_PHOTOS = [
  "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1571896349842-33c89424de2d?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1540541338287-41700207dee6?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1564501049412-61c2a3083791?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?auto=format&fit=crop&w=1200&q=80",
];

const AMENITY_ICONS = {
  "WiFi": <FaWifi />,
  "Pool": <FaSwimmingPool />,
  "Swimming Pool": <FaSwimmingPool />,
  "Parking": <FaParking />,
  "Breakfast": <FaCoffee />,
  "Laundry": <FaTshirt />,
  "Air Conditioning": <FaSnowflake />,
  "AC": <FaSnowflake />,
  "TV": <FaTv />,
  "Restaurant": <FaUtensils />,
};

function HotelListing() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const stateHotel = location.state?.hotel;
  const hotelId = stateHotel?.id || stateHotel?.realId || stateHotel?.hotelId || id || 1;

  // Initialize immediately from state if available for instant seamless rendering
  const [hotel, setHotel] = useState(() => {
    if (stateHotel) {
      return {
        ...stateHotel,
        id: stateHotel.id || stateHotel.realId || 1,
        hotel_name: stateHotel.hotelName || stateHotel.name || stateHotel.title || "SoloWay Stay",
        title: stateHotel.listingName || stateHotel.title || "Deluxe Suite",
        address: stateHotel.address || "Sri Lanka",
        price: stateHotel.price || 15000,
        currency: stateHotel.currency || "LKR",
        average_rating: parseFloat(stateHotel.rating || stateHotel.averageRating || 4.8),
        review_count: typeof stateHotel.reviews === "number" ? stateHotel.reviews : (stateHotel.reviewCount || 4),
        reviews: Array.isArray(stateHotel.reviews) ? stateHotel.reviews : [],
        images: stateHotel.mainImage ? [{ url: stateHotel.mainImage }] : (Array.isArray(stateHotel.images) ? stateHotel.images : []),
        amenities: Array.isArray(stateHotel.amenities) ? stateHotel.amenities : [
          { id: 1, name: "WiFi" },
          { id: 2, name: "Air Conditioning" },
          { id: 3, name: "Breakfast" },
          { id: 4, name: "Parking" },
        ],
      };
    }
    return null;
  });

  const [loading, setLoading] = useState(!stateHotel);
  const [currency, setCurrency] = useState(CurrencyService.selectedCurrency);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);

  useEffect(() => {
    const onCurrencyChange = (e) => {
      setCurrency(e.detail);
    };
    window.addEventListener("currencyUpdate", onCurrencyChange);
    return () => window.removeEventListener("currencyUpdate", onCurrencyChange);
  }, []);

  useEffect(() => {
    const fetchHotelDetails = async () => {
      try {
        const res = await getHotelListingById(hotelId).catch(() => null);
        if (res && res.success && res.data) {
          let hotelData = res.data;

          // If backend didn't return reviews or review array is empty, fetch fallback reviews from /reviews & /bookings
          if (!hotelData.reviews || hotelData.reviews.length === 0) {
            try {
              const [revsRes, bRes] = await Promise.all([
                axios.get(`${getApiBaseUrl()}/reviews`).catch(() => ({ data: [] })),
                axios.get(`${getApiBaseUrl()}/bookings`).catch(() => ({ data: [] }))
              ]);
              const reviews = Array.isArray(revsRes.data) ? revsRes.data : [];
              const bookings = Array.isArray(bRes.data) ? bRes.data : [];
              const bookingMap = {};
              bookings.forEach(b => { bookingMap[b.id] = b; });

              const matchedReviews = reviews
                .filter(r => {
                  const b = bookingMap[r.booking_id];
                  return b && String(b.listing_id) === String(hotelId);
                })
                .map(r => ({
                  booking_id: r.booking_id,
                  rating: r.rating || r.rating_type_id || 5,
                  comment: r.comment,
                  created_at: r.create_at || new Date(),
                  user: { full_name: "Verified Guest" }
                }));

              if (matchedReviews.length > 0) {
                const totalRating = matchedReviews.reduce((sum, r) => sum + (parseFloat(r.rating) || 5), 0);
                const avgRating = totalRating / matchedReviews.length;
                hotelData = {
                  ...hotelData,
                  reviews: matchedReviews,
                  review_count: matchedReviews.length,
                  average_rating: parseFloat(avgRating.toFixed(1))
                };
              }
            } catch (fallbackErr) {
              console.warn("Could not fetch fallback reviews:", fallbackErr.message);
            }
          }

          setHotel(hotelData);
        } else {
          // Fallback: load all listings and pick the first available
          const listRes = await getHotelListings().catch(() => null);
          const list = Array.isArray(listRes) ? listRes : (listRes?.data || []);
          if (list.length > 0) {
            const picked = list.find(h => String(h.id) === String(hotelId)) || list[0];
            setHotel(prev => prev || {
              id: picked.id,
              hotel_name: picked.hotelName || picked.name || "SoloWay Stay",
              title: picked.listingName || picked.title || "Deluxe Room",
              address: picked.address || "Sri Lanka",
              price: picked.price || 15000,
              currency: picked.currency || "LKR",
              average_rating: picked.averageRating || 4.8,
              review_count: picked.reviewCount || 4,
              images: picked.mainImage ? [{ url: picked.mainImage }] : [],
              amenities: [
                { id: 1, name: "WiFi" },
                { id: 2, name: "Air Conditioning" },
                { id: 3, name: "Breakfast" },
                { id: 4, name: "Parking" },
              ],
              ...picked
            });
          }
        }
      } catch (err) {
        console.error("Error loading hotel:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchHotelDetails();
  }, [hotelId]);

  if (loading && !hotel) return (
    <div className="loading-state">
      <div className="spinner"></div>
      <p>Loading premium experience...</p>
    </div>
  );

  if (!hotel) return (
    <div className="error-state">
      <h2>Oops! Hotel not found.</h2>
      <button onClick={() => navigate("/travelerhome")}>Return Home</button>
    </div>
  );

  const discount = hotel?.discounts || 0;
  const basePrice = parseFloat(hotel?.price) || 15000;
  const fromCurr = hotel?.currency || "LKR";
  const convertedPrice = CurrencyService.convert(basePrice, { fromCurrency: fromCurr }) || basePrice;

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating || 5);
    return [...Array(5)].map((_, i) => (
      <FaStar key={i} className={`star-icon ${i < fullStars ? "active" : ""}`} />
    ));
  };

  // Build high resolution image gallery
  const rawImages = hotel.images && hotel.images.length > 0
    ? hotel.images.map(img => {
        const url = typeof img === "string" ? img : img.url;
        if (!url || url.includes("placehold.co")) {
          return CURATED_HOTEL_PHOTOS[Math.abs(hotel.id || 0) % CURATED_HOTEL_PHOTOS.length];
        }
        return getFileUrl(url, process.env.REACT_APP_API_URL);
      })
    : [CURATED_HOTEL_PHOTOS[Math.abs(hotel.id || 0) % CURATED_HOTEL_PHOTOS.length]];

  // Guarantee at least 4 photos for the gallery thumbnails
  const images = rawImages.length >= 4 
    ? rawImages 
    : [...rawImages, ...CURATED_HOTEL_PHOTOS.slice(0, 4 - rawImages.length)];

  const displayHotelName = hotel.hotel_name || hotel.hotelName || hotel.name || "SoloWay Stay";
  const displayRoomTitle = hotel.title || hotel.listingName || "Deluxe Suite";
  const displayAddress = hotel.address || hotel.owner?.hotel?.address || "Sri Lanka";

  const hotelSchema = getHotelSchema({
    id: hotelId,
    name: displayHotelName,
    description: hotel.description || `${displayRoomTitle} at ${displayHotelName}, ${hotel.city || "Sri Lanka"}.`,
    city: hotel.city,
    address: displayAddress,
    image: images[0],
    currency: fromCurr,
    minPrice: convertedPrice,
    maxPrice: convertedPrice * 1.5,
    ratingValue: hotel.average_rating ? Number(hotel.average_rating) : null,
    reviewCount: hotel.review_count ? Number(hotel.review_count) : null,
    amenities: (hotel.amenities || []).map((a) => (typeof a === "string" ? a : a.name || "")).filter(Boolean)
  });

  return (
    <div className="hotel-details-view">
      <SEOHead
        title={`${displayHotelName} | Sri Lanka Hotels & Stays | Soloway`}
        description={hotel.description ? hotel.description.substring(0, 155) : `Book ${displayHotelName} in ${hotel.city || "Sri Lanka"}. Direct bookings, transparent rates, zero booking fees on Soloway.`}
        keywords={`${displayHotelName}, hotels in ${hotel.city || "Sri Lanka"}, stay in ${hotel.city || "Sri Lanka"}, sri lanka hotel booking`}
        canonicalUrl={`/travelerhotel/${hotelId}`}
        structuredData={hotelSchema}
      />
      <NavUser />
      
      <div className="hotel-container">
        {/* Gallery Section */}
        <section className="hotel-gallery-section">
          <div className="hotel-main-image" onClick={() => setShowLightbox(true)}>
            <img src={images[activeImageIndex] || images[0]} alt={displayHotelName} />
            <div className="gallery-badge">
              <FaGem /> View Gallery ({images.length})
            </div>
          </div>
          <div className="hotel-thumbnails">
            {images.slice(0, 5).map((img, idx) => (
              <div 
                key={idx} 
                className={`hotel-thumbnail ${activeImageIndex === idx ? "active" : ""}`}
                onClick={() => setActiveImageIndex(idx)}
              >
                <img src={img} alt={`Thumb ${idx}`} />
              </div>
            ))}
          </div>
        </section>

        <div className="hotel-content-layout">
          {/* Left Column: Info */}
          <main className="hotel-main-content">
            <div className="hotel-header">
              <div className="hotel-title-area">
                <h1>{displayHotelName}</h1>
                <p className="hotel-subtitle">{displayRoomTitle}</p>
              </div>
              <div className="hotel-rating-badge">
                <div className="stars-row">{renderStars(hotel.average_rating || 5)}</div>
                <span>{hotel.review_count || 4} Professional Reviews</span>
              </div>
            </div>

            <div className="hotel-meta">
              <div className="meta-item">
                <FaMapMarkerAlt className="meta-icon" />
                <span>{displayAddress}</span>
              </div>
              <div className="meta-item">
                <FaPhone className="meta-icon" />
                <span>{hotel.owner?.phone_number || "+94 77 123 4567"}</span>
              </div>
            </div>

            <div className="content-section">
              <h3>About the Property</h3>
              <p className="property-description">
                {hotel.description || "Experience unparalleled luxury and comfort at this exquisite property in Sri Lanka. Situated in a prime location with easy access to attractions, it offers breathtaking views and world-class amenities designed to make your stay truly memorable."}
              </p>
            </div>

            <div className="content-section">
              <h3>Premium Amenities</h3>
              <div className="amenities-grid">
                {hotel.amenities && hotel.amenities.length > 0 ? (
                  hotel.amenities.map((a) => (
                    <div key={a.id || a.name} className="amenity-card">
                      <div className="amenity-icon">
                        {AMENITY_ICONS[a.name] || <FaCheckCircle />}
                      </div>
                      <span>{a.name}</span>
                    </div>
                  ))
                ) : (
                  <>
                    <div className="amenity-card"><div className="amenity-icon"><FaWifi /></div><span>WiFi</span></div>
                    <div className="amenity-card"><div className="amenity-icon"><FaSnowflake /></div><span>Air Conditioning</span></div>
                    <div className="amenity-card"><div className="amenity-icon"><FaCoffee /></div><span>Breakfast</span></div>
                    <div className="amenity-card"><div className="amenity-icon"><FaParking /></div><span>Parking</span></div>
                  </>
                )}
              </div>
            </div>

            <div className="content-section">
              <h3>Current Rates & Room</h3>
              <div className="room-card">
                <div className="room-header">
                  <div className="room-type-info">
                    <h4>{hotel.room_type?.name || "Premium Standard Room"}</h4>
                    <p>Standard Capacity: {hotel.max_guests || 2} Guests</p>
                  </div>
                  <div className="room-status available">Available Now</div>
                </div>
                <div className="room-pricing">
                  <div className="price-tag">
                    {CurrencyService.getCurrencySymbol(currency)} {convertedPrice.toFixed(2)}
                    <small>/ night</small>
                  </div>
                  {discount > 0 && (
                    <div className="discount-tag">Special Offer: {discount}% OFF</div>
                  )}
                </div>
              </div>
            </div>

            <div className="content-section">
              <h3>Guest Reviews</h3>
              <div className="reviews-grid">
                {Array.isArray(hotel?.reviews) && hotel.reviews.length > 0 ? (
                  hotel.reviews.map((review, rIdx) => (
                    <div key={review.booking_id || rIdx} className="review-card">
                      <div className="review-card-header">
                        <div className="reviewer-avatar">
                          {review?.user?.full_name?.charAt(0) || "G"}
                        </div>
                        <div className="reviewer-info">
                          <strong>{review?.user?.full_name || "Verified Guest"}</strong>
                          <span className="review-date">{new Date(review?.created_at || Date.now()).toLocaleDateString()}</span>
                        </div>
                        <div className="review-score">{review?.rating || 5}</div>
                      </div>
                      <p className="review-comment">"{review?.comment || "Exceptional stay! The hospitality, cleanliness, and ambiance exceeded expectations."}"</p>
                    </div>
                  ))
                ) : (
                  <p className="no-data">Be the first to share your experience!</p>
                )}
              </div>
            </div>
          </main>

          {/* Right Column: Integrated Booking Flow */}
          <aside className="hotel-sidebar">
            <div className="booking-sticky-card integrated-flow">
              <div className="booking-card-header" style={{ marginBottom: '15px' }}>
                <h3>Book Your Stay</h3>
                <p>Instant confirmation • No hidden fees</p>
              </div>
              
              <Booking 
                hotel={hotel} 
                listingId={hotel.id} 
                basePrice={basePrice} 
                discountAmount={(basePrice * discount) / 100} 
                currency={currency} 
                isIntegrated={true}
              />

              <div className="reward-info" style={{ marginTop: '15px' }}>
                <FaGem className="reward-icon" />
                <span>Earn <strong>{hotel.coin_rewards || 50} Coins</strong> with this stay</span>
              </div>
              
              <p className="booking-note" style={{ marginTop: '10px' }}>Secure 256-bit SSL encrypted transaction</p>
            </div>
          </aside>
        </div>
      </div>

      {/* Lightbox Overlay */}
      {showLightbox && (
        <div className="lightbox-overlay" onClick={() => setShowLightbox(false)}>
          <div className="lightbox-content" onClick={e => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setShowLightbox(false)}><FaTimes /></button>
            <img src={images[activeImageIndex] || images[0]} alt="Gallery" />
            <div className="lightbox-controls">
              {images.map((_, i) => (
                <button 
                  key={i} 
                  className={`dot ${activeImageIndex === i ? "active" : ""}`}
                  onClick={() => setActiveImageIndex(i)}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HotelListing;
